/**
 * AtlasBankModule — BANKING (2026-09-24): "implement loans and banking for your credits". The rules are the mirror
 * lib/bank_rules.ts (byte-identical with the server's); the server is reducers/banking.ts + lib/bank_ops.ts.
 *
 *   THE WINDOW (menu bar → Bank; E at a town's bank terminal; the loan chip): pick a bank (the Commons Reserve, or a
 *     city's bank), then Account · Transfer · Savings · Loans · Credit · Vault · City. Cash in and out, a loan's signing
 *     and pledge, the box and the auction block need a BRANCH (the terminal, or the city bank's building); transfers,
 *     term deposits and payments are online banking and work anywhere.
 *   THE CHIP: a loan with money due, or an installment falling due inside six hours, shows at the right edge.
 *   Every number the window prints is the server's row or the mirror's arithmetic on it — never a guess.
 * Levers: `__bank`.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeWindow, type UiWindow } from '../lib/ui_window.js';
import * as R from '../lib/bank_rules.js';
import { TERMINAL_USE_M } from '../lib/activity_catalog.js';
import { SCHEMATIC_BY_KEY } from '../lib/schematic_catalog.js';
import { el, btn, input, select, tabs, head, chip, muted, field, card, act, debounced, ms } from './town/town_ui.js';

type Ts = { microsSinceUnixEpoch: bigint };
type Id = { toHexString(): string; isEqual?(o: unknown): boolean };
interface BankRow {
  bankId: bigint; kind: string; cityId: bigint; name: string; status: string; baseBps: number; reserveRatioBps: number; reserveFactorBps: number;
  supplyIndex: number; scaledDeposits: number; savingsBps: number; utilization: number; loansOutstanding: number; depositsTotal: number;
  vaultCash: number; equity: number; avgLoanBps: number; writtenOff: bigint; interestEarned: number; capitalFloor: bigint; owesReserve: number;
  lastAccrualAt: Ts;
}
interface AccountRow { accountKey: string; bankId: bigint; identity: Id; scaled: number; netDeposited: bigint; openedAt: Ts }
interface TxnRow { txnId: bigint; identity: Id; bankId: bigint; kind: string; amount: bigint; memo: string; at: Ts }
interface CdRow { cdId: bigint; bankId: bigint; identity: Id; principal: bigint; rateBps: number; termDays: number; openedAt: Ts; maturesAt: Ts }
interface LoanRow {
  loanId: bigint; bankId: bigint; borrower: Id; principal: bigint; fee: bigint; rateBps: number; termDays: number; installment: bigint;
  balance: number; accruedAt: Ts; due: number; dueSince: Ts; dueLate: boolean; nextDueAt: Ts; finalDueAt: Ts; missed: number;
  lateCount: number; paidTotal: bigint; secured: boolean; autopay: boolean; state: string; createdAt: Ts; closedAt: Ts;
}
interface CustodyRow { entryId: bigint; bankId: bigint; identity: Id; purpose: string; loanId: bigint; kind: string; itemKey: string; name: string; quality: number; qty: number; appraisal: bigint }
interface RepoRow { lotId: bigint; bankId: bigint; kind: string; itemKey: string; name: string; quality: number; qty: number; appraisal: bigint; listedAt: Ts }
interface CreditRow { identity: Id; onTime: number; late: number; defaults: number; repaidLoans: number; kindsMask: number; openedAt: Ts; bannedUntil: Ts }
interface ItemRow { itemId: bigint; owner: Id; itemKey: string; name: string; quality: number; serial: string }
interface StackRow { stackId: bigint; owner: Id; familyKey: string; name: string; qty: number }

const TABS = [
  { key: 'account', label: 'Account' }, { key: 'transfer', label: 'Transfer' }, { key: 'savings', label: 'Savings' },
  { key: 'loans', label: 'Loans' }, { key: 'credit', label: 'Credit' }, { key: 'vault', label: 'Vault' }, { key: 'city', label: 'City bank' },
];
const RESERVE = BigInt(R.RESERVE_BANK_ID);
const CHIP_SOON_MS = 6 * 3600_000;

const fmt = (n: number): string => Math.round(n).toLocaleString('en-US');
const pct = (bps: number): string => `${(bps / 100).toFixed(2)} %`;
const inDays = (msLeft: number): string => {
  const s = Math.max(0, Math.round(msLeft / 1000));
  if (s >= 2 * 86400) return `${Math.floor(s / 86400)} d ${Math.floor((s % 86400) / 3600)} h`;
  if (s >= 3600) return `${Math.floor(s / 3600)} h ${Math.floor((s % 3600) / 60)} m`;
  return `${Math.floor(s / 60)} m ${s % 60} s`;
};

export class AtlasBankModule implements IModule {
  readonly name = 'AtlasBank';
  private win: UiWindow | null = null;
  private tab = 'account';
  private bankId: bigint = RESERVE;
  private subbed = false;
  private pendingRender = false;
  private chipEl: HTMLDivElement | null = null;
  private tick: number | null = null;
  /** Form state survives re-renders (a row arriving must never wipe what the player is typing). */
  private f = {
    amount: '', toUser: '', toBank: '0', memo: '', cdAmount: '', cdTerm: '7', loanAmount: '500', loanTerm: '7', autopay: true,
    pledgeItems: new Set<string>(), pledgeStacks: new Map<string, number>(), payAmount: '', boxQty: new Map<string, string>(),
    charterName: '', charterCapital: '2000', base: '', rr: '', rf: '', profit: '',
  };
  private readonly rerender = debounced(() => this.render(), 200);

  init(_app: App): void {
    bus.on('atlas:open-bank', (p) => {
      if (p?.tab) this.tab = p.tab;
      if (p?.stationId) this.bankId = RESERVE;
      this.open();
    });
    bus.on('atlas:account-ready', () => this.subscribe());
    if (atlas.conn) this.subscribe();
    this.chipEl = el('div', 'position:fixed;right:16px;top:150px;z-index:31;display:none;cursor:pointer;padding:6px 10px;border-radius:var(--ax-radius);background:var(--ax-bg2);border:1px solid var(--ax-line2);font:12.5px var(--ax-font);color:var(--ax-text);max-width:280px');
    this.chipEl.addEventListener('click', () => { this.tab = 'loans'; this.open(); });
    document.body.appendChild(this.chipEl);
    this.tick = window.setInterval(() => { this.updateChip(); if (this.win?.isOpen()) this.softRender(); }, 5000);
    (window as unknown as { __bank?: unknown }).__bank = {
      state: () => this.state(),
      open: (tab?: string, bankId?: number) => { if (tab) this.tab = tab; if (bankId !== undefined) this.bankId = BigInt(bankId); this.open(); },
      close: () => this.win?.setOpen(false),
      atBranch: (bankId?: number) => this.atBranch(bankId === undefined ? this.bankId : BigInt(bankId)),
      quote: (principal: number, termDays: number, secured: boolean) => this.quote(principal, termDays, secured),
      score: () => this.score(),
      rendered: () => this.win?.body.textContent ?? '',
    };
  }

  // ── Data ──────────────────────────────────────────────────────────────────────
  private subscribe(): void {
    const conn = atlas.conn;
    if (!conn || this.subbed) return;
    this.subbed = true;
    try {
      const db = conn.db as unknown as Record<string, { onInsert(f: () => void): void; onUpdate?(f: () => void): void; onDelete(f: () => void): void }>;
      for (const k of ['bank', 'bankAccount', 'bankTxn', 'bankTermDeposit', 'bankLoan', 'bankCustody', 'bankRepoLot', 'creditFile']) {
        const t = db[k];
        if (!t) continue;
        t.onInsert(() => this.rerender());
        t.onUpdate?.(() => this.rerender());
        t.onDelete(() => this.rerender());
      }
      conn.subscriptionBuilder().subscribe([
        'SELECT * FROM bank', 'SELECT * FROM bank_account', 'SELECT * FROM bank_txn', 'SELECT * FROM bank_term_deposit',
        'SELECT * FROM bank_loan', 'SELECT * FROM bank_custody', 'SELECT * FROM bank_repo_lot', 'SELECT * FROM credit_file',
        'SELECT * FROM player_item WHERE owner = :sender', 'SELECT * FROM player_resource WHERE owner = :sender',
        "SELECT * FROM structure WHERE structure_type = 'bank'", 'SELECT * FROM city',
      ]);
    } catch { /* pre-publish */ }
  }

  private db(): Record<string, { iter(): Iterable<unknown> }> | null {
    return (atlas.conn?.db as unknown as Record<string, { iter(): Iterable<unknown> }>) ?? null;
  }
  private rows<T>(table: string): T[] {
    const t = this.db()?.[table];
    if (!t) return [];
    try { return [...t.iter()] as T[]; } catch { return []; }
  }
  private me(): string { return atlas.identityHex ?? ''; }
  private mine<T extends { identity?: Id; borrower?: Id; owner?: Id }>(list: T[]): T[] {
    const me = this.me();
    return list.filter((r) => (r.identity ?? r.borrower ?? r.owner)?.toHexString() === me);
  }
  private banks(): BankRow[] {
    return this.rows<BankRow>('bank').sort((a, b) => (a.bankId < b.bankId ? -1 : 1));
  }
  private bank(): BankRow | null {
    return this.banks().find((b) => b.bankId === this.bankId) ?? this.banks().find((b) => b.bankId === RESERVE) ?? null;
  }
  private account(bankId: bigint): AccountRow | null {
    return this.mine(this.rows<AccountRow>('bankAccount')).find((a) => a.bankId === bankId) ?? null;
  }
  /** The balance a row implies now: the stored index grown at the savings rate since the last accrual. */
  private balance(b: BankRow, a: AccountRow | null): number {
    if (!a) return 0;
    const dt = Math.max(0, Date.now() / 1000 - ms(b.lastAccrualAt) / 1000);
    return Math.floor(a.scaled * R.growIndex(b.supplyIndex, b.savingsBps, dt) + 1e-9);
  }
  private loanNow(l: LoanRow): number {
    const dt = Math.max(0, Date.now() / 1000 - ms(l.accruedAt) / 1000);
    return l.balance > 0 ? l.balance * Math.pow(1 + R.dailyRate(l.rateBps), dt / R.DAY_S) : 0;
  }
  private myLoans(): LoanRow[] { return this.mine(this.rows<LoanRow>('bankLoan')); }
  private activeLoans(): LoanRow[] { return this.myLoans().filter((l) => l.state === 'active'); }

  private score(): R.CreditScore & { facts: R.CreditFacts } {
    const me = this.me();
    const f = this.rows<CreditRow>('creditFile').find((r) => r.identity.toHexString() === me);
    const now = Date.now();
    let open = 0, fresh = 0;
    for (const l of this.myLoans()) {
      if (l.state === 'active') open += this.loanNow(l);
      if (now - ms(l.createdAt) < 7 * 86400_000) fresh++;
    }
    const rep = this.rows<{ identity: Id; trustworthiness: number; traitorUntil: Ts }>('reputation').find((r) => r.identity.toHexString() === me);
    let kinds = 0;
    for (let v = f?.kindsMask ?? 0; v; v &= v - 1) kinds++;
    const facts: R.CreditFacts = {
      onTime: f?.onTime ?? 0, late: f?.late ?? 0, defaults: f?.defaults ?? 0, repaidLoans: f?.repaidLoans ?? 0, openBalance: open,
      ageDays: f ? (now - ms(f.openedAt)) / 86400_000 : 0, newLoans7d: fresh, kinds,
      trust: rep ? rep.trustworthiness : 50, traitor: !!rep && ms(rep.traitorUntil) > now,
    };
    return { ...R.creditScore(facts), facts };
  }

  private terms(b: BankRow): R.RateTerms {
    return b.kind === 'reserve' ? { ...R.RESERVE_TERMS, baseBps: b.baseBps } : R.cityTerms(b.baseBps, b.reserveRatioBps / 10_000, b.reserveFactorBps / 10_000);
  }
  private quote(principal: number, termDays: number, secured: boolean): R.LoanQuote & { freeCash: number } | null {
    const b = this.bank();
    if (!b) return null;
    const t = this.terms(b);
    const uAfter = R.utilization(b.loansOutstanding + principal, b.depositsTotal, b.equity);
    const rate = R.loanRateBps(uAfter, t, this.score().score, secured);
    const free = R.freeCash({ vaultCash: b.vaultCash, loans: b.loansOutstanding, deposits: b.depositsTotal, owesReserve: b.owesReserve }, t);
    return { ...R.quoteLoan(principal, termDays, rate), freeCash: free };
  }

  /** Whether the player stands at a branch of this bank (the server is the judge; this is the window's gate). */
  private atBranch(bankId: bigint): boolean {
    const db = this.db();
    if (!db) return false;
    const px = atlas.localX, pz = atlas.localZ;
    if (bankId === RESERVE) {
      for (const s of this.rows<{ kind: string; x: number; z: number }>('activityStation')) {
        if (s.kind === 'bank_terminal' && Math.hypot(s.x - px, s.z - pz) <= TERMINAL_USE_M) return true;
      }
      return false;
    }
    const b = this.banks().find((x) => x.bankId === bankId);
    if (!b) return false;
    const civic = this.rows<{ structureId: bigint; cityId: bigint; civicType: string; status: string }>('civicStructure');
    const structs = this.rows<{ structureId: bigint; posX: number; posZ: number }>('structure');
    for (const c of civic) {
      if (c.cityId !== b.cityId || c.civicType !== 'bank' || c.status !== 'active') continue;
      const s = structs.find((x) => x.structureId === c.structureId);
      if (s && Math.hypot(s.posX - px, s.posZ - pz) <= R.CITY_BRANCH_M) return true;
    }
    return false;
  }

  private myCity(): { cityId: bigint; name: string; treasury: bigint; status: string } | null {
    const me = this.me();
    return this.rows<{ cityId: bigint; name: string; treasury: bigint; status: string; mayor: Id }>('city').find((c) => c.mayor.toHexString() === me) ?? null;
  }

  private state(): Record<string, unknown> {
    const b = this.bank();
    const s = this.score();
    return {
      open: !!this.win?.isOpen(), tab: this.tab, bankId: Number(this.bankId), banks: this.banks().map((x) => ({ id: Number(x.bankId), name: x.name, status: x.status })),
      balance: b ? this.balance(b, this.account(b.bankId)) : 0, savingsBps: b?.savingsBps ?? 0, atBranch: this.atBranch(this.bankId),
      loans: this.activeLoans().map((l) => ({ id: Number(l.loanId), bank: Number(l.bankId), owed: Math.ceil(this.loanNow(l)), due: l.due, missed: l.missed, rate: l.rateBps })),
      score: s.score, band: R.scoreBand(s.score), cds: this.mine(this.rows<CdRow>('bankTermDeposit')).length,
      box: this.mine(this.rows<CustodyRow>('bankCustody')).filter((c) => c.purpose === 'box').length,
      pledged: this.mine(this.rows<CustodyRow>('bankCustody')).filter((c) => c.purpose === 'collateral').length,
      repo: this.rows<RepoRow>('bankRepoLot').length, chip: this.chipEl?.style.display !== 'none' ? this.chipEl?.textContent : null,
    };
  }

  // ── The chip ──────────────────────────────────────────────────────────────────
  private updateChip(): void {
    if (!this.chipEl) return;
    const now = Date.now();
    let text = '';
    for (const l of this.activeLoans()) {
      if (l.due >= 0.5) { text = `Loan #${l.loanId}: ${fmt(Math.ceil(l.due))} ⓒ due${l.dueLate ? ' — LATE' : ''}${l.autopay ? ' (autopay)' : ''}`; break; }
      const next = ms(l.nextDueAt) - now;
      if (next > 0 && next < CHIP_SOON_MS) text = `Loan #${l.loanId}: ${fmt(Number(l.installment))} ⓒ due in ${inDays(next)}`;
    }
    const show = text !== '' && !atlas.spaceFlight;
    if ((this.chipEl.style.display !== 'none') !== show) this.chipEl.style.display = show ? 'block' : 'none';
    if (show && this.chipEl.textContent !== text) this.chipEl.textContent = text;
  }

  // ── The window ────────────────────────────────────────────────────────────────
  private open(): void {
    if (!this.win) {
      this.win = makeWindow({ id: 'bank', title: 'Bank', icon: 'bank', width: 600, minWidth: 420, height: 620 });
      this.win.body.addEventListener('focusout', () => { if (this.pendingRender) window.setTimeout(() => this.softRender(), 0); });
    }
    this.subscribe();
    this.win.setOpen(true);
    this.render();
  }

  /** Re-render unless the player is typing in the window (then once they leave the field). */
  private softRender(): void {
    const a = document.activeElement as HTMLElement | null;
    if (a && this.win?.body.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT' || a.tagName === 'TEXTAREA')) { this.pendingRender = true; return; }
    this.pendingRender = false;
    this.render();
  }

  private render(): void {
    const body = this.win?.body;
    if (!body || !this.win?.isOpen()) return;
    const a = document.activeElement as HTMLElement | null;
    if (a && body.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT' || a.tagName === 'TEXTAREA')) { this.pendingRender = true; return; }
    this.pendingRender = false;
    const scroll = body.scrollTop;
    body.replaceChildren();
    const wrap = el('div', 'display:flex;flex-direction:column;gap:10px;padding:12px 14px');
    const b = this.bank();
    if (!b) { wrap.appendChild(muted('The Commons Reserve opens with the world\'s next heartbeat.')); body.appendChild(wrap); return; }
    // The bank picker + where you stand.
    const top = el('div', 'display:flex;gap:8px;align-items:center;flex-wrap:wrap');
    const pick = select(this.banks().filter((x) => x.status === 'open' || x.bankId === this.bankId).map((x) => ({ value: String(x.bankId), label: x.name })), String(b.bankId));
    pick.addEventListener('change', () => { this.bankId = BigInt(pick.value); pick.blur(); this.render(); });
    top.appendChild(pick);
    const branch = this.atBranch(b.bankId);
    top.appendChild(chip(branch ? 'At a branch' : 'Online banking', branch ? 'good' : 'info'));
    top.appendChild(chip(`wallet ${fmt(Number(atlas.walletSov))} ⓒ`));
    wrap.appendChild(top);
    const shown = TABS.filter((t) => t.key !== 'city' || this.myCity());
    wrap.appendChild(tabs(shown, this.tab, (k) => { this.tab = k; this.render(); }));
    const pane = el('div', 'display:flex;flex-direction:column;gap:10px');
    pane.dataset.bankTab = this.tab;
    if (this.tab === 'account') this.paneAccount(pane, b, branch);
    else if (this.tab === 'transfer') this.paneTransfer(pane, b);
    else if (this.tab === 'savings') this.paneSavings(pane, b);
    else if (this.tab === 'loans') this.paneLoans(pane, b, branch);
    else if (this.tab === 'credit') this.paneCredit(pane);
    else if (this.tab === 'vault') this.paneVault(pane, b, branch);
    else if (this.tab === 'city') this.paneCity(pane);
    wrap.appendChild(pane);
    body.appendChild(wrap);
    body.scrollTop = scroll;
  }

  private bind(i: HTMLInputElement | HTMLSelectElement, key: keyof AtlasBankModule['f']): void {
    i.addEventListener('input', () => { (this.f as Record<string, unknown>)[key] = i.value; });
    i.addEventListener('change', () => { (this.f as Record<string, unknown>)[key] = i.value; });
  }
  private num(v: string): number { const n = Math.floor(Number(v)); return Number.isFinite(n) && n > 0 ? n : 0; }

  private paneAccount(p: HTMLElement, b: BankRow, branch: boolean): void {
    const acct = this.account(b.bankId);
    const bal = this.balance(b, acct);
    const c = card();
    const big = el('div', 'font:600 26px var(--ax-mono);color:var(--ax-hi)', `${fmt(bal)} ⓒ`);
    big.className = 'ax-num';
    big.dataset.bankBalance = String(bal);
    c.appendChild(big);
    const earned = acct ? bal - Number(acct.netDeposited) : 0;
    const chips = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
    chips.appendChild(chip(`savings ${pct(b.savingsBps)} a week`, 'good'));
    if (acct) chips.appendChild(chip(`interest earned ${fmt(Math.max(0, earned))} ⓒ`));
    if (b.kind === 'city') chips.appendChild(chip(`insured by the Reserve to ${fmt(R.INSURED_MAX)} ⓒ`, 'info'));
    c.appendChild(chips);
    c.appendChild(muted(acct ? `Open since ${new Date(ms(acct.openedAt)).toUTCString().slice(5, 16)}. Interest reaches your account every minute, paid out of what ${b.name}'s borrowers pay.` : `No account at ${b.name} yet — it opens with your first deposit.`));
    p.appendChild(c);
    // Cash in / out.
    const amt = input('Amount', this.f.amount, 'number', '130px');
    this.bind(amt, 'amount');
    const dep = btn('Deposit', () => { const n = this.num(this.f.amount); if (n) void act(atlas.conn?.reducers.bankDeposit({ bankId: b.bankId, amount: BigInt(n) }), `Deposited ${fmt(n)} ⓒ`); }, true);
    const wd = btn('Withdraw', () => { const n = this.num(this.f.amount); if (n) void act(atlas.conn?.reducers.bankWithdraw({ bankId: b.bankId, amount: BigInt(n) }), `Withdrew ${fmt(n)} ⓒ`); });
    dep.disabled = !branch; wd.disabled = !branch;
    p.appendChild(field('Cash', amt, dep, wd));
    if (!branch) p.appendChild(muted(b.kind === 'reserve' ? 'Cash goes in and out at a bank terminal — every town and city has one by its market.' : `Cash goes in and out inside ${b.name}'s building.`));
    // The bank's books (open to everyone).
    p.appendChild(head(`${b.name} — the books`));
    const books = el('div', 'display:grid;grid-template-columns:repeat(3, minmax(0,1fr));gap:6px');
    const cell = (label: string, v: string) => { const x = card(); x.appendChild(muted(label)); const n = el('div', 'font:600 14px var(--ax-mono);color:var(--ax-text)', v); x.appendChild(n); return x; };
    books.appendChild(cell('Deposits', `${fmt(b.depositsTotal)} ⓒ`));
    books.appendChild(cell('Loans out', `${fmt(b.loansOutstanding)} ⓒ`));
    books.appendChild(cell('Vault', `${fmt(b.vaultCash)} ⓒ`));
    books.appendChild(cell('Equity', `${fmt(b.equity)} ⓒ`));
    books.appendChild(cell('Lent out', `${(b.utilization * 100).toFixed(1)} %`));
    books.appendChild(cell('Borrowing from', pct(R.borrowRateBps(b.utilization, this.terms(b)))));
    p.appendChild(books);
    p.appendChild(muted(`It keeps ${(b.reserveRatioBps / 100).toFixed(0)} % of deposits in the vault and takes ${(b.reserveFactorBps / 100).toFixed(0)} % of loan interest as its margin; savers share the rest. Written off to date: ${fmt(Number(b.writtenOff))} ⓒ.`));
    // The statement.
    p.appendChild(head('Statement'));
    const txns = this.mine(this.rows<TxnRow>('bankTxn')).sort((x, y) => (x.txnId > y.txnId ? -1 : 1)).slice(0, 25);
    if (!txns.length) p.appendChild(muted('Nothing yet.'));
    for (const t of txns) {
      const r = el('div', 'display:flex;gap:8px;align-items:baseline;border-bottom:1px solid var(--ax-line);padding:3px 0');
      r.appendChild(el('span', 'min-width:84px;color:var(--ax-muted);font:11.5px var(--ax-mono)', new Date(ms(t.at)).toISOString().slice(5, 16).replace('T', ' ')));
      const amtEl = el('span', `min-width:86px;text-align:right;font:12px var(--ax-mono);color:${Number(t.amount) >= 0 ? 'var(--ax-good)' : 'var(--ax-bad)'}`, `${Number(t.amount) >= 0 ? '+' : ''}${fmt(Number(t.amount))}`);
      r.appendChild(amtEl);
      r.appendChild(el('span', 'color:var(--ax-soft);font-size:12.5px', `${t.kind.replace(/_/g, ' ')} — ${t.memo}`));
      p.appendChild(r);
    }
  }

  private paneTransfer(p: HTMLElement, b: BankRow): void {
    const bal = this.balance(b, this.account(b.bankId));
    p.appendChild(muted(`From your account at ${b.name} (${fmt(bal)} ⓒ). Inside one bank a transfer is free; to another bank it is a wire of 1 % (2–100 ⓒ). Works anywhere.`));
    const who = input('Username', this.f.toUser, 'text', '180px'); this.bind(who, 'toUser');
    const amt = input('Amount', this.f.amount, 'number', '120px'); this.bind(amt, 'amount');
    const dest = select([{ value: '0', label: 'the same bank' }, ...this.banks().filter((x) => x.status === 'open' && x.bankId !== b.bankId).map((x) => ({ value: String(x.bankId), label: x.name }))], this.f.toBank);
    this.bind(dest, 'toBank');
    const memo = input('Memo (optional)', this.f.memo, 'text', '220px'); this.bind(memo, 'memo');
    p.appendChild(field('To', who));
    p.appendChild(field('At', dest));
    p.appendChild(field('Amount', amt));
    p.appendChild(field('Memo', memo));
    const n = this.num(this.f.amount);
    const fee = this.f.toBank !== '0' && n ? R.wireFee(n) : 0;
    p.appendChild(muted(n ? `They receive ${fmt(n)} ⓒ; your account pays ${fmt(n + fee)} ⓒ${fee ? ` (wire fee ${fee} ⓒ)` : ''}.` : 'Enter an amount.'));
    p.appendChild(btn('Send', () => {
      const a = this.num(this.f.amount);
      if (!a || !this.f.toUser.trim()) return;
      void act(atlas.conn?.reducers.bankTransfer({ fromBankId: b.bankId, toUsername: this.f.toUser.trim(), toBankId: BigInt(this.f.toBank || '0'), amount: BigInt(a), memo: this.f.memo }), `Sent ${fmt(a)} ⓒ to ${this.f.toUser.trim()}`);
    }, true));
  }

  private paneSavings(p: HTMLElement, b: BankRow): void {
    p.appendChild(muted(`A term deposit locks credits from your account for a term at a rate fixed today — the savings rate plus 15 % for each week of term. Break it early and the principal comes back; the interest does not.`));
    const amt = input('Amount', this.f.cdAmount, 'number', '120px'); this.bind(amt, 'cdAmount');
    const term = select(R.CD_TERMS_DAYS.map((d) => ({ value: String(d), label: `${d} days` })), this.f.cdTerm);
    term.addEventListener('change', () => { this.f.cdTerm = term.value; term.blur(); this.render(); });
    const n = this.num(this.f.cdAmount);
    const days = Number(this.f.cdTerm) || 7;
    const rate = R.termDepositRateBps(b.savingsBps, days);
    const atEnd = n ? Math.floor(R.termDepositValue(n, rate, days, days * R.DAY_S)) : 0;
    p.appendChild(field('Lock', amt, term));
    p.appendChild(muted(`${pct(rate)} a week${n ? ` → ${fmt(atEnd)} ⓒ at maturity (+${fmt(atEnd - n)} ⓒ)` : ''}.${b.savingsBps < 0.01 ? ' The bank pays nothing while no one borrows from it.' : ''}`));
    p.appendChild(btn('Open term deposit', () => { const a = this.num(this.f.cdAmount); if (a) void act(atlas.conn?.reducers.openTermDeposit({ bankId: b.bankId, amount: BigInt(a), termDays: days }), `Locked ${fmt(a)} ⓒ for ${days} days`); }, true));
    p.appendChild(head('Your term deposits'));
    const cds = this.mine(this.rows<CdRow>('bankTermDeposit')).sort((x, y) => (x.cdId < y.cdId ? -1 : 1));
    if (!cds.length) p.appendChild(muted('None.'));
    const now = Date.now();
    for (const cd of cds) {
      const c = card();
      const v = R.termDepositValue(Number(cd.principal), cd.rateBps, cd.termDays, (now - ms(cd.openedAt)) / 1000);
      const bank = this.banks().find((x) => x.bankId === cd.bankId);
      c.appendChild(el('div', 'color:var(--ax-text)', `#${cd.cdId} · ${fmt(Number(cd.principal))} ⓒ at ${bank?.name ?? 'a bank'} · ${cd.termDays} days at ${pct(cd.rateBps)} a week`));
      const r = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
      r.appendChild(chip(`worth ${fmt(v)} ⓒ now`, 'good'));
      r.appendChild(chip(`matures in ${inDays(ms(cd.maturesAt) - now)}`));
      r.appendChild(btn('Break early', () => { void act(atlas.conn?.reducers.breakTermDeposit({ cdId: cd.cdId }), 'Broken — the principal is back in your account'); }));
      c.appendChild(r);
      p.appendChild(c);
    }
  }

  private paneLoans(p: HTMLElement, b: BankRow, branch: boolean): void {
    const now = Date.now();
    const active = this.activeLoans();
    p.appendChild(head('Your loans'));
    if (!active.length) p.appendChild(muted('No open loans.'));
    const custody = this.mine(this.rows<CustodyRow>('bankCustody'));
    for (const l of active) {
      const c = card();
      c.dataset.loanId = String(l.loanId);
      const owed = Math.ceil(this.loanNow(l));
      const bank = this.banks().find((x) => x.bankId === l.bankId);
      c.appendChild(el('div', 'color:var(--ax-text)', `#${l.loanId} · ${fmt(Number(l.principal))} ⓒ from ${bank?.name ?? 'a bank'} · ${pct(l.rateBps)} a week · ${l.secured ? 'secured' : 'unsecured'}`));
      const r = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      r.appendChild(chip(`owed ${fmt(owed)} ⓒ`, 'warn'));
      r.appendChild(chip(l.due >= 0.5 ? `DUE ${fmt(Math.ceil(l.due))} ⓒ${l.dueLate ? ' — late' : ''}` : `next ${fmt(Number(l.installment))} ⓒ in ${inDays(ms(l.nextDueAt) - now)}`, l.due >= 0.5 ? 'bad' : ''));
      r.appendChild(chip(`final ${new Date(ms(l.finalDueAt)).toUTCString().slice(5, 22)}`));
      if (l.missed) r.appendChild(chip(`${l.missed} missed — ${R.DEFAULT_MISSED} is a default`, 'bad'));
      c.appendChild(r);
      const pledged = custody.filter((x) => x.purpose === 'collateral' && x.loanId === l.loanId);
      if (pledged.length) c.appendChild(muted(`Pledged: ${pledged.map((x) => (x.kind === 'stack' ? `${x.qty} × ${x.name}` : x.name)).join(', ')} — returned when it is repaid, auctioned if it defaults.`));
      const pay = input('Amount', this.f.payAmount, 'number', '110px'); this.bind(pay, 'payAmount');
      const row = el('div', 'display:flex;gap:6px;align-items:center;flex-wrap:wrap');
      row.appendChild(btn(l.due >= 0.5 ? `Pay what is due (${fmt(Math.ceil(l.due))})` : 'Pay next installment', () => {
        const a = l.due >= 0.5 ? Math.ceil(l.due) : Number(l.installment);
        void act(atlas.conn?.reducers.repayLoan({ loanId: l.loanId, amount: BigInt(a), fromAccount: false }), `Paid ${fmt(a)} ⓒ`);
      }, true));
      row.appendChild(pay);
      row.appendChild(btn('Pay from wallet', () => { const a = this.num(this.f.payAmount); if (a) void act(atlas.conn?.reducers.repayLoan({ loanId: l.loanId, amount: BigInt(a), fromAccount: false }), `Paid ${fmt(a)} ⓒ`); }));
      row.appendChild(btn('Pay from account', () => { const a = this.num(this.f.payAmount); if (a) void act(atlas.conn?.reducers.repayLoan({ loanId: l.loanId, amount: BigInt(a), fromAccount: true }), `Paid ${fmt(a)} ⓒ from your account`); }));
      row.appendChild(btn('Pay off', () => { void act(atlas.conn?.reducers.repayLoan({ loanId: l.loanId, amount: BigInt(owed), fromAccount: false }), 'Loan repaid'); }));
      const ap = btn(l.autopay ? 'Autopay: on' : 'Autopay: off', () => { void act(atlas.conn?.reducers.setLoanAutopay({ loanId: l.loanId, on: !l.autopay }), ''); });
      row.appendChild(ap);
      c.appendChild(row);
      p.appendChild(c);
    }
    // Apply.
    p.appendChild(head(`Borrow from ${b.name}`));
    const s = this.score();
    const lim = R.unsecuredLimit(s.score, s.thin);
    let openUnsecured = 0;
    for (const l of active) if (!l.secured) openUnsecured += this.loanNow(l);
    const file = this.rows<CreditRow>('creditFile').find((r) => r.identity.toHexString() === this.me());
    const banned = file && ms(file.bannedUntil) > now;
    const items = this.mine(this.rows<ItemRow>('playerItem'));
    const stacks = this.mine(this.rows<StackRow>('playerResource'));
    let appraisal = 0;
    for (const it of items) if (this.f.pledgeItems.has(String(it.itemId))) appraisal += R.appraiseItem(SCHEMATIC_BY_KEY[it.itemKey]?.complexity ?? 1, it.quality);
    for (const st of stacks) { const q = this.f.pledgeStacks.get(String(st.stackId)); if (q) appraisal += R.appraiseStack(Math.min(q, st.qty)); }
    const secured = appraisal > 0 || this.f.pledgeItems.size > 0 || this.f.pledgeStacks.size > 0;
    const amt = input('Amount', this.f.loanAmount, 'number', '110px');
    amt.addEventListener('change', () => { this.f.loanAmount = amt.value; this.render(); });
    this.bind(amt, 'loanAmount');
    const term = select(R.LOAN_TERMS_DAYS.map((d) => ({ value: String(d), label: `${d} days` })), this.f.loanTerm);
    term.addEventListener('change', () => { this.f.loanTerm = term.value; term.blur(); this.render(); });
    const auto = el('label', 'display:flex;gap:4px;align-items:center;color:var(--ax-soft)');
    const cb = document.createElement('input'); cb.type = 'checkbox'; cb.checked = this.f.autopay;
    cb.addEventListener('change', () => { this.f.autopay = cb.checked; });
    auto.append(cb, document.createTextNode('autopay each day'));
    p.appendChild(field('Loan', amt, term, auto));
    const P = this.num(this.f.loanAmount);
    const days = Number(this.f.loanTerm) || 7;
    const q = P ? this.quote(P, days, secured) : null;
    const quoteEl = el('div', 'display:flex;flex-direction:column;gap:4px');
    quoteEl.dataset.bankQuote = q ? String(q.rateBps) : '';
    if (q) {
      const qc = el('div', 'display:flex;gap:6px;flex-wrap:wrap');
      qc.appendChild(chip(`${pct(q.rateBps)} a week`, 'warn'));
      qc.appendChild(chip(`${fmt(q.installment)} ⓒ a day × ${days}`));
      qc.appendChild(chip(`interest ${fmt(q.interest)} ⓒ`));
      qc.appendChild(chip(`fee ${fmt(q.fee)} ⓒ`));
      qc.appendChild(chip(`you receive ${fmt(q.disbursed)} ⓒ`, 'good'));
      quoteEl.appendChild(qc);
    }
    const why: string[] = [];
    if (banned) why.push(`No bank lends to you until ${new Date(ms(file!.bannedUntil)).toUTCString().slice(5, 22)} (a default).`);
    if (active.length >= R.MAX_OPEN_LOANS) why.push(`You hold ${R.MAX_OPEN_LOANS} open loans already.`);
    if (secured) why.push(`Your pledge appraises at ${fmt(appraisal)} ⓒ and secures up to ${fmt(R.securedLimit(appraisal))} ⓒ (a quarter off the rate).`);
    else {
      const left = Math.max(0, lim - Math.ceil(openUnsecured));
      why.push(`At a score of ${s.score} (${R.scoreBand(s.score)}) you may borrow ${fmt(left)} ⓒ unsecured${s.thin ? ' — capped at 250 until you repay a first loan' : ''}.`);
      if (!file || now - ms(file.openedAt) < R.THIN_FILE_MIN_AGE_S * 1000) why.push('An unsecured loan needs a banking record at least a day old — open an account first, or pledge goods.');
    }
    if (q && P > q.freeCash) why.push(`${b.name} can lend ${fmt(q.freeCash)} ⓒ right now (its reserve).`);
    for (const w of why) quoteEl.appendChild(muted(w));
    p.appendChild(quoteEl);
    // The pledge picker.
    const pick = el('details', 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px 8px');
    pick.open = secured;
    pick.appendChild(el('summary', 'cursor:pointer;color:var(--ax-soft)', `Pledge goods (secured loan) — ${this.f.pledgeItems.size + this.f.pledgeStacks.size} chosen`));
    const list = el('div', 'display:flex;flex-direction:column;gap:3px;max-height:180px;overflow:auto;margin-top:6px');
    for (const it of items) {
      const id = String(it.itemId);
      const lab = el('label', 'display:flex;gap:6px;align-items:center;color:var(--ax-text);font-size:12.5px');
      const x = document.createElement('input'); x.type = 'checkbox'; x.checked = this.f.pledgeItems.has(id);
      x.addEventListener('change', () => { if (x.checked) this.f.pledgeItems.add(id); else this.f.pledgeItems.delete(id); this.render(); });
      lab.append(x, document.createTextNode(`${it.name} (q${it.quality}) — ${fmt(R.appraiseItem(SCHEMATIC_BY_KEY[it.itemKey]?.complexity ?? 1, it.quality))} ⓒ`));
      list.appendChild(lab);
    }
    for (const st of stacks) {
      const id = String(st.stackId);
      const row = el('div', 'display:flex;gap:6px;align-items:center;color:var(--ax-text);font-size:12.5px');
      const qi = input('qty', this.f.pledgeStacks.get(id) ? String(this.f.pledgeStacks.get(id)) : '', 'number', '64px');
      qi.addEventListener('change', () => { const n = Math.min(this.num(qi.value), st.qty); if (n) this.f.pledgeStacks.set(id, n); else this.f.pledgeStacks.delete(id); this.render(); });
      row.append(qi, document.createTextNode(`of ${st.qty} × ${st.name} — ${fmt(R.appraiseStack(st.qty))} ⓒ for all`));
      list.appendChild(row);
    }
    if (!items.length && !stacks.length) list.appendChild(muted('Your inventory is empty.'));
    pick.appendChild(list);
    p.appendChild(pick);
    const sign = btn('Sign the loan', () => {
      if (!q) return;
      const itemIds = [...this.f.pledgeItems].map((x) => BigInt(x));
      const st = [...this.f.pledgeStacks].map(([k, qty]) => ({ stackId: BigInt(k), qty }));
      void act(atlas.conn?.reducers.applyForLoan({ bankId: b.bankId, principal: BigInt(P), termDays: days, autopay: this.f.autopay, maxRateBps: q.rateBps + 2, itemIds, stacks: st }),
        `Loan signed — ${fmt(q.disbursed)} ⓒ is in your wallet`, () => { this.f.pledgeItems.clear(); this.f.pledgeStacks.clear(); });
    }, true);
    sign.disabled = !branch || !q || !!banned || active.length >= R.MAX_OPEN_LOANS;
    p.appendChild(sign);
    if (!branch) p.appendChild(muted(b.kind === 'reserve' ? 'A loan is signed at a bank terminal — the pledge is handed over there.' : `A loan is signed inside ${b.name}'s building.`));
    // History.
    const done = this.myLoans().filter((l) => l.state !== 'active').slice(-6);
    if (done.length) {
      p.appendChild(head('Past loans'));
      for (const l of done) p.appendChild(muted(`#${l.loanId} · ${fmt(Number(l.principal))} ⓒ · ${l.state}${l.state === 'defaulted' ? ` — ${fmt(l.balance)} ⓒ written off` : ''} · ${l.lateCount} late`));
    }
  }

  private paneCredit(p: HTMLElement): void {
    const s = this.score();
    const c = card();
    const big = el('div', 'font:600 34px var(--ax-mono);color:var(--ax-hi)', String(s.score));
    big.dataset.creditScore = String(s.score);
    c.appendChild(big);
    c.appendChild(el('div', 'color:var(--ax-soft)', `${R.scoreBand(s.score)} · 300–850 · unsecured limit ${fmt(R.unsecuredLimit(s.score, s.thin))} ⓒ${s.thin ? ' (thin file)' : ''}`));
    p.appendChild(c);
    p.appendChild(head('What makes it'));
    for (const fct of s.factors) {
      const r = el('div', 'display:flex;flex-direction:column;gap:2px');
      r.appendChild(el('div', 'display:flex;justify-content:space-between;color:var(--ax-text);font-size:12.5px', `${fct.label} · ${Math.round(fct.weight * 100)} %`));
      const bar = el('div', 'height:6px;border-radius:var(--ax-radius);background:var(--ax-bg3);overflow:hidden');
      bar.appendChild(el('div', `height:100%;width:${Math.round(fct.value * 100)}%;background:var(--ax-accent)`));
      r.appendChild(bar);
      p.appendChild(r);
    }
    const f = s.facts;
    p.appendChild(muted(`On time ${f.onTime} · late ${f.late} · defaults ${f.defaults} · loans repaid ${f.repaidLoans} · owed now ${fmt(f.openBalance)} ⓒ · file ${f.ageDays.toFixed(1)} days old · new loans this week ${f.newLoans7d} · products used ${f.kinds}/5.`));
    p.appendChild(muted(`Trustworthiness ${f.trust} moves it ${s.trustMod >= 0 ? '+' : ''}${s.trustMod}${f.traitor ? ' (a Traitor tag)' : ''}. A default costs 10 Trustworthiness and bars credit for two weeks.`));
  }

  private paneVault(p: HTMLElement, b: BankRow, branch: boolean): void {
    const custody = this.mine(this.rows<CustodyRow>('bankCustody'));
    const box = custody.filter((x) => x.purpose === 'box' && x.bankId === b.bankId);
    p.appendChild(head(`Safe deposit box at ${b.name} (${box.length}/${R.BOX_CAPACITY})`));
    p.appendChild(muted(`Storing costs a ${R.BOX_FEE} ⓒ handling fee; taking out is free. Both at a branch.${branch ? '' : ' You are not at one.'}`));
    for (const e of box) {
      const r = el('div', 'display:flex;gap:6px;align-items:center');
      r.appendChild(el('span', 'color:var(--ax-text);flex:1', e.kind === 'stack' ? `${e.qty} × ${e.name}` : `${e.name} (q${e.quality})`));
      const take = btn('Take', () => { void act(atlas.conn?.reducers.boxTake({ entryId: e.entryId, qty: 0 }), 'Taken'); });
      take.disabled = !branch;
      r.appendChild(take);
      p.appendChild(r);
    }
    const store = el('details', 'border:1px solid var(--ax-line);border-radius:var(--ax-radius);padding:6px 8px');
    store.appendChild(el('summary', 'cursor:pointer;color:var(--ax-soft)', 'Store from your inventory'));
    const list = el('div', 'display:flex;flex-direction:column;gap:3px;max-height:180px;overflow:auto;margin-top:6px');
    for (const it of this.mine(this.rows<ItemRow>('playerItem'))) {
      const r = el('div', 'display:flex;gap:6px;align-items:center');
      r.appendChild(el('span', 'color:var(--ax-text);flex:1;font-size:12.5px', `${it.name} (q${it.quality})`));
      const s = btn('Store', () => { void act(atlas.conn?.reducers.boxStoreItem({ bankId: b.bankId, itemId: it.itemId }), 'Stored'); });
      s.disabled = !branch;
      r.appendChild(s);
      list.appendChild(r);
    }
    for (const st of this.mine(this.rows<StackRow>('playerResource'))) {
      const id = String(st.stackId);
      const r = el('div', 'display:flex;gap:6px;align-items:center');
      r.appendChild(el('span', 'color:var(--ax-text);flex:1;font-size:12.5px', `${st.qty} × ${st.name}`));
      const q = input('qty', this.f.boxQty.get(id) ?? String(st.qty), 'number', '64px');
      q.addEventListener('input', () => this.f.boxQty.set(id, q.value));
      const s = btn('Store', () => { const n = Math.min(this.num(this.f.boxQty.get(id) ?? String(st.qty)), st.qty); if (n) void act(atlas.conn?.reducers.boxStoreStack({ bankId: b.bankId, stackId: st.stackId, qty: n }), 'Stored'); });
      s.disabled = !branch;
      r.append(q, s);
      list.appendChild(r);
    }
    store.appendChild(list);
    p.appendChild(store);
    const pledged = custody.filter((x) => x.purpose === 'collateral');
    if (pledged.length) {
      p.appendChild(head('Pledged against your loans'));
      for (const e of pledged) p.appendChild(muted(`${e.kind === 'stack' ? `${e.qty} × ${e.name}` : e.name} — loan #${e.loanId}, appraised ${fmt(Number(e.appraisal))} ⓒ`));
    }
    // The auction block (public).
    const lots = this.rows<RepoRow>('bankRepoLot').filter((x) => x.bankId === b.bankId);
    p.appendChild(head(`The auction block at ${b.name}`));
    p.appendChild(muted('Pledges seized from defaulted loans, at a fixed price that falls to 60 % after 3 days and 40 % after 7; unsold lots are scrapped at 14 days.'));
    if (!lots.length) p.appendChild(muted('Nothing on the block.'));
    const now = Date.now();
    for (const lot of lots) {
      const r = el('div', 'display:flex;gap:6px;align-items:center');
      const price = R.repoPrice(Number(lot.appraisal), (now - ms(lot.listedAt)) / 1000);
      r.appendChild(el('span', 'color:var(--ax-text);flex:1;font-size:12.5px', `${lot.kind === 'stack' ? `${lot.qty} × ${lot.name}` : `${lot.name} (q${lot.quality})`} — appraised ${fmt(Number(lot.appraisal))} ⓒ`));
      const buy = btn(`Buy ${fmt(price)} ⓒ`, () => { void act(atlas.conn?.reducers.buyRepoLot({ lotId: lot.lotId }), 'Bought at auction'); });
      buy.disabled = !branch;
      r.appendChild(buy);
      p.appendChild(r);
    }
  }

  private paneCity(p: HTMLElement): void {
    const c = this.myCity();
    if (!c) { p.appendChild(muted('Only a mayor charters a city bank.')); return; }
    const id = BigInt(R.CITY_BANK_ID_BASE) + c.cityId;
    const b = this.banks().find((x) => x.bankId === id);
    if (!b || b.status !== 'open') {
      p.appendChild(muted(`Charter ${c.name}'s own bank: a Bank building must stand in the city, and its capital (at least ${fmt(R.CITY_BOUNDS.minCapital)} ⓒ) comes from the treasury (${fmt(Number(c.treasury))} ⓒ). You set its rates; its profit returns to the treasury; the Reserve insures its depositors to ${fmt(R.INSURED_MAX)} ⓒ each for a premium of ${R.INSURANCE_PREMIUM_BPS} bps of deposits a week, and lends to it when its vault runs short.`));
      const nm = input('Bank name', this.f.charterName || `${c.name} Savings & Loan`, 'text', '220px'); this.bind(nm, 'charterName');
      const cap = input('Capital', this.f.charterCapital, 'number', '120px'); this.bind(cap, 'charterCapital');
      p.appendChild(field('Name', nm));
      p.appendChild(field('Capital', cap));
      p.appendChild(btn('Charter the bank', () => {
        const capital = this.num(this.f.charterCapital);
        void act(atlas.conn?.reducers.charterCityBank({ cityId: c.cityId, name: this.f.charterName || `${c.name} Savings & Loan`, capital: BigInt(capital) }), 'Chartered', () => { this.bankId = id; });
      }, true));
      return;
    }
    const s: R.BankSheet = { vaultCash: b.vaultCash, loans: b.loansOutstanding, deposits: b.depositsTotal, owesReserve: b.owesReserve };
    const t = this.terms(b);
    const avail = Math.max(0, Math.floor(Math.min(b.equity - Number(b.capitalFloor) - b.owesReserve, R.freeCash(s, t))));
    p.appendChild(el('div', 'color:var(--ax-text)', `${b.name} — equity ${fmt(b.equity)} ⓒ on ${fmt(Number(b.capitalFloor))} ⓒ of capital; owes the Reserve ${fmt(b.owesReserve)} ⓒ; ${fmt(avail)} ⓒ of profit can be drawn.`));
    const base = input('bps a week', this.f.base || String(b.baseBps), 'number', '90px'); this.bind(base, 'base');
    const rr = input('%', this.f.rr || String(b.reserveRatioBps / 100), 'number', '70px'); this.bind(rr, 'rr');
    const rf = input('%', this.f.rf || String(b.reserveFactorBps / 100), 'number', '70px'); this.bind(rf, 'rf');
    p.appendChild(field(`Base rate (${R.CITY_BOUNDS.baseBps[0]}–${R.CITY_BOUNDS.baseBps[1]} bps)`, base));
    p.appendChild(field('Reserve ratio (10–50 %)', rr));
    p.appendChild(field('Margin (10–50 %)', rf));
    p.appendChild(btn('Set terms', () => {
      void act(atlas.conn?.reducers.setCityBankTerms({ bankId: id, baseBps: Math.round(Number(this.f.base || b.baseBps)), reserveRatioBps: Math.round(Number(this.f.rr || b.reserveRatioBps / 100) * 100), reserveFactorBps: Math.round(Number(this.f.rf || b.reserveFactorBps / 100) * 100) }), 'Terms set');
    }, true));
    const pr = input('Amount', this.f.profit, 'number', '110px'); this.bind(pr, 'profit');
    p.appendChild(field('Draw profit', pr, btn('Pay into the treasury', () => { const a = this.num(this.f.profit); if (a) void act(atlas.conn?.reducers.cityBankProfit({ bankId: id, amount: BigInt(a) }), `${fmt(a)} ⓒ to the treasury`); })));
    p.appendChild(btn('Close the bank', () => { void act(atlas.conn?.reducers.closeCityBank({ bankId: id }), 'Closed — its cash is back in the treasury'); }));
  }

  dispose(): void {
    if (this.tick !== null) window.clearInterval(this.tick);
    this.chipEl?.remove();
    this.win?.root.remove();
  }
}
